import React, { useState } from 'react';
import confetti from 'canvas-confetti';
import { FaTrophy, FaMedal, FaStar, FaHandshake, FaUsers, FaCalendarAlt, FaUser, FaEdit, FaTrash } from 'react-icons/fa';
import styles from './AwardCard.module.css';

const AwardCard = ({ award, showFullDetails = false, onEdit, onDelete, onClick, compact = false }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const getAwardIcon = (awardType) => {
    switch (awardType) {
      case 'individual': return <FaTrophy />;
      case 'team': return <FaUsers />;
      case 'position': return <FaMedal />;
      case 'statistical': return <FaStar />;
      case 'character': return <FaHandshake />;
      default: return <FaTrophy />;
    }
  };

  const getAwardColor = (awardType) => {
    switch (awardType) {
      case 'individual': return '#FFD700';
      case 'team': return '#C0C0C0';
      case 'position': return '#CD7F32';
      case 'statistical': return '#FF8C00';
      case 'character': return '#90EE90';
      default: return '#800080';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!award || !award.awardTemplateId) {
    return null;
  }

  const { awardTemplateId, recipients, awardedAt, awardedByName, notes, eventId } = award;
  const awardColor = getAwardColor(awardTemplateId.awardType);
  
  // Compact version for grid view
  if (compact) {
    const firstRecipient = recipients[0];
    const recipientName = firstRecipient?.playerName || 'Unknown';
    const moreCount = recipients.length - 1;
    
    const handleClick = (e) => {
      // Trigger confetti animation
      const rect = e.currentTarget.getBoundingClientRect();
      const x = (rect.left + rect.width / 2) / window.innerWidth;
      const y = (rect.top + rect.height / 2) / window.innerHeight;
      
      confetti({
        particleCount: 50,
        spread: 60,
        origin: { x, y },
        colors: [awardColor, '#FFD700', '#A3E635'],
        gravity: 1.2,
        scalar: 0.8
      });
      
      // Call the onClick handler to open modal
      if (onClick) onClick();
    };
    
    return (
      <div 
        className={styles.compactCard} 
        style={{ borderLeftColor: awardColor }}
        onClick={handleClick}
      >
        {/* Icon + Award Title (Same Row) */}
        <div className={styles.compactHeader}>
          <div className={styles.compactIcon} style={{ color: awardColor }}>
            {getAwardIcon(awardTemplateId.awardType)}
          </div>
          <h3 className={styles.compactTitle}>{awardTemplateId.awardName}</h3>
        </div>
        
        <div className={styles.compactContent}>
          {/* Player Avatar + Name */}
          <div className={styles.playerSection}>
            <div className={styles.playerAvatar}>
              {recipientName.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className={styles.playerInfo}>
              <p className={styles.compactPlayer}>
                {recipientName}
              </p>
              {moreCount > 0 && (
                <p className={styles.moreRecipients}>+{moreCount} more</p>
              )}
            </div>
          </div>
          
          <p className={styles.compactDate}>
            {formatDate(awardedAt || award.dateAwarded || award.createdAt)}
          </p>
          <span 
            className={styles.compactBadge}
            style={{ backgroundColor: awardColor }}
          >
            {awardTemplateId.awardType}
          </span>
        </div>
        
        {/* Manager Actions - Hover Overlay */}
        {(onEdit || onDelete) && (
          <div className={styles.hoverActions}>
            {onEdit && (
              <button 
                className={styles.editIconBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(award);
                }}
                title="Edit Award"
              >
                <FaEdit />
              </button>
            )}
            {onDelete && (
              <button 
                className={styles.deleteIconBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(award._id);
                }}
                title="Delete Award"
              >
                <FaTrash />
              </button>
            )}
          </div>
        )}
      </div>
    );
  }
  
  // Full details version (original)
  return (
    <div className={styles.awardCard} style={{ borderLeftColor: awardColor }}>
      <div className={styles.awardHeader}>
        <div className={styles.awardIcon} style={{ color: awardColor }}>
          {getAwardIcon(awardTemplateId.awardType)}
        </div>
        <div className={styles.awardInfo}>
          <h3 className={styles.awardName}>{awardTemplateId.awardName}</h3>
          <p className={styles.awardDescription}>{awardTemplateId.description}</p>
        </div>
        <div className={styles.awardType}>
          <span 
            className={styles.typeBadge}
            style={{ backgroundColor: awardColor }}
          >
            {awardTemplateId.awardType}
          </span>
        </div>
        {(onEdit || onDelete) && (
          <div className={styles.awardActions}>
            {onEdit && (
              <button 
                className={styles.editButton}
                onClick={() => onEdit(award)}
                title="Edit Award"
              >
                <FaEdit />
              </button>
            )}
            {onDelete && (
              <button 
                className={styles.deleteButton}
                onClick={() => setShowDeleteConfirm(true)}
                title="Delete Award"
              >
                <FaTrash />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation */}
      {showDeleteConfirm && (
        <div className={styles.deleteConfirmation}>
          <p>Are you sure you want to delete this award?</p>
          <div className={styles.confirmActions}>
            <button 
              className={styles.confirmDelete}
              onClick={() => {
                onDelete(award._id);
                setShowDeleteConfirm(false);
              }}
            >
              Delete
            </button>
            <button 
              className={styles.cancelDelete}
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className={styles.awardContent}>
        {/* Recipients */}
        <div className={styles.recipients}>
          <div className={styles.recipientsHeader}>
            <FaUser className={styles.recipientsIcon} />
            <span>Recipients</span>
          </div>
          <div className={styles.recipientsList}>
            {recipients.map((recipient, index) => (
              <div key={index} className={styles.recipient}>
                <span className={styles.recipientName}>{recipient.playerName}</span>
                {recipient.teamName && (
                  <span className={styles.recipientTeam}>({recipient.teamName})</span>
                )}
                {recipient.roleName && (
                  <span className={styles.recipientRole}>- {recipient.roleName}</span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Event Information */}
        {eventId && typeof eventId === 'object' && (
          <div className={styles.eventInfo}>
            <div className={styles.eventHeader}>
              <FaCalendarAlt className={styles.eventIcon} />
              <span>Event</span>
            </div>
            <div className={styles.eventDetails}>
              <span className={styles.eventName}>{eventId.title || 'Event'}</span>
              {eventId.dateTime?.start && (
                <span className={styles.eventDate}>
                  {formatDate(eventId.dateTime.start)}
                </span>
              )}
              {eventId.type && (
                <span className={styles.eventType}> • {eventId.type}</span>
              )}
            </div>
          </div>
        )}

        {/* Award Details */}
        <div className={styles.awardDetails}>
          <div className={styles.awardMeta}>
            <div className={styles.awardedBy}>
              <span className={styles.metaLabel}>Awarded by:</span>
              <span className={styles.metaValue}>{awardedByName}</span>
            </div>
            <div className={styles.awardedAt}>
              <span className={styles.metaLabel}>Date:</span>
              <span className={styles.metaValue}>{formatDate(awardedAt)}</span>
            </div>
            {showFullDetails && (
              <div className={styles.awardedTime}>
                <span className={styles.metaLabel}>Time:</span>
                <span className={styles.metaValue}>{formatTime(awardedAt)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        {notes && (
          <div className={styles.notes}>
            <div className={styles.notesHeader}>
              <span>Notes</span>
            </div>
            <p className={styles.notesContent}>{notes}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AwardCard;
